import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

export const Header = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const changeLang = async (code) => {
    setLang(code);
    if (user) {
      try {
        await api.post("/auth/language", { language: code });
      } catch (err) {
        // Best-effort: language preference is local-first and already applied.
        console.debug("Language preference sync skipped:", err?.message || err);
      }
    }
  };

  const links = [
    { to: "/", label: t("nav.book") },
    ...(user ? [{ to: "/bookings", label: t("nav.bookings") }] : []),
    ...(user?.role === "vendor" ? [{ to: "/driver", label: t("nav.driver") }] : []),
    ...(user?.role === "admin" ? [{ to: "/admin", label: t("nav.admin") }] : []),
    ...(user?.role === "admin" ? [{ to: "/admin/live", label: t("nav.live") }] : []),
    ...(user?.role === "agency" ? [{ to: "/agency", label: t("nav.agency") }] : []),
    ...(user ? [{ to: "/support", label: t("nav.support") }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-slate-200/70">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <img
            src="/vaya-logo.png"
            alt="Vaya Hotel Transfers"
            className="h-11 w-auto sm:h-14"
            data-testid="brand-logo-img"
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <Link
              key={l.to + l.label}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-sea"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <select
            data-testid="language-switcher"
            value={lang}
            onChange={(e) => changeLang(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 outline-none transition-colors duration-200 focus:border-sea"
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.short}
              </option>
            ))}
          </select>

          {user ? (
            <div className="hidden items-center gap-3 md:flex">
              <Link
                to="/account"
                data-testid="header-account-link"
                className="max-w-[140px] truncate text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-sea"
              >
                {user.name}
              </Link>
              <Button
                data-testid="logout-btn"
                variant="outline"
                size="sm"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
              >
                <LogOut size={14} className="mr-1.5" /> {t("nav.logout")}
              </Button>
            </div>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <Button
                data-testid="header-login-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
              >
                {t("nav.login")}
              </Button>
              <Button
                data-testid="header-register-btn"
                size="sm"
                className="bg-sea hover:bg-sea-dark"
                onClick={() => navigate("/register")}
              >
                {t("nav.register")}
              </Button>
            </div>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={"m" + l.to + l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-slate-700"
              >
                {l.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/account" data-testid="mobile-account-link" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-700">
                  {t("account.title")}
                </Link>
                <button
                  data-testid="mobile-logout-btn"
                  className="text-left text-sm font-medium text-destructive"
                  onClick={() => {
                    logout();
                    setOpen(false);
                    navigate("/");
                  }}
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium text-sea">
                  {t("nav.login")}
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium text-sea">
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
